import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from "react-redux";
import { TextField, Select } from '@mui/material';
// utilities
import { formatEnum } from "../../utils/string";
import { updateUserWithRole } from '../../utils/api/user_apis';
import { getOrgRoles } from '../../utils/api/role_apis';
import { STATUS } from '../../utils/api/sharedfunction';
// components
import { UserEmailSchema } from '../../Validation/ReusableValidation';
import { CommonFooter, FormSchemas, FormWrapper } from '../FormComponent';
import ModalWrapper from '../ModalWrapper';
import RoleEnum from '../../enums/RoleEnum';

const EditUserModal = ({ isOpen, onClose, onError, onUpdate, userData }) => {
  const [userOrgRoles, setUserOrgRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const userRoleNames = useSelector((state) => state.auth.user?.roles) || [];
  const isTrialOrgAdmin = userRoleNames.includes(RoleEnum.TRIAL_ORG_ADMIN.name);
  const formRef = useRef();

  const roleOptions = useMemo(
    () =>
      isTrialOrgAdmin
        ? userOrgRoles.filter((r) => r.name === RoleEnum.TRIAL_USER.name)
        : userOrgRoles,
    [isTrialOrgAdmin, userOrgRoles]
  );

  useEffect(() => {
    if (isOpen) {
      fetchRole();
    }
  }, [isOpen]);

  const handleFormSubmit = async () => {
    if (formRef.current) await formRef.current.submit();
  };

  const fetchRole = async () => {
    const response = await getOrgRoles();
    if (response.status === STATUS.SUCCESS) {
      setUserOrgRoles(response?.data?.roles || []);
    } else {
      console.error('Failed to fetch roles of (organization scope):', response.error);
    }
  };

  const handleConfirmEdit = async (data) => {
    const selectedRole = roleOptions.find((role) => role.id === data.role) || userOrgRoles.find((role) => role.id === data.role);
    try {
      setLoading(true);
      const response = await updateUserWithRole({
        userId: userData.id, 
        username: data.username,
        email: data.email,
        roles: [data.role],
        organization: (selectedRole?.name && selectedRole?.name !== RoleEnum.SUPER_ADMIN.name) ? userData?.organization?.id : null, //"super_admin" role is not in the organization
      });

      if (response.status === STATUS.SUCCESS) {
        onUpdate();
        onClose();
      } else {
        onError(response.error || 'Failed to update user', 'error');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      onError(error.error || 'Failed to update user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const buttons = [
    {
      label: 'Cancel',
      variant: 'outline',
      className: 'primary',
      onClick: onClose,
      disabled: loading,
    },
    {
      label: 'Update',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: handleFormSubmit,
      disabled: loading,
      loading: loading,
    },
  ];

  const fields = [
    {
      name: 'username',
      type: 'text',
      label: 'Username',
      placeholder: 'Enter username',
      required: true,
    },
    {
      name: 'email',
      type: 'email',
      label: 'Email',
      placeholder: 'Enter email address',
      required: true,
    },
    {
      name: 'role',
      type: 'select',
      label: 'Role',
      required: true,
      options: roleOptions.map((role) => ({
        value: role.id,
        label: role.name,
      })),
    },
  ];

  const defaultRoleId =
    isTrialOrgAdmin && roleOptions.length > 0
      ? (userData?.roles?.[0] && roleOptions.some((r) => r.id === userData.roles[0].id)
          ? userData.roles[0].id
          : roleOptions[0].id)
      : userData?.roles?.[0]?.id || '';

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Update User"
      size="small"
      footer={
        <CommonFooter
          buttons={buttons}
          align="right"
        />
      }
      children={
        <FormWrapper
          ref={formRef}
          schema={FormSchemas.userCreation}
          fields={fields}
          onSubmit={handleConfirmEdit}
          defaultValues={{
            username: userData?.username || '',
            email: userData?.email || '',
            role: defaultRoleId,
          }}
        />
      }
    />
  );
};

export default EditUserModal;
